import { useEffect, useMemo } from 'react';
import omit from 'lodash-es/omit';
import {
  IntakeFormQueries,
  prepareIntakeFormStoreInfoFromLocationList,
  useIntakeFormShallowStore,
  useIntakeFormStore,
} from '@frontend/api-intake-form';
import { getUser, isWeaveUser } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { ONBOARDING_DASHBOARD_PATH, useOnboardingDashboardConfig } from '@frontend/onboarding-dashboard';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { AnySearch, useMatchRoute, useNavigate, useSearch, type AnyRoute } from '@frontend/weave-router';
import { useAlert } from '@frontend/design-system';
import { INTAKE_FORM_PATHS, INTAKE_FORM_PATH_V2, INTAKE_FORM_V2_FEATURE_FLAG, NWX_DASHBOARD_PATH } from '../constants';

// Custom Hook used to determine whether a redirect to the onboarding page should occur for the current location.
export const useOnboardingRedirect = () => {
  const { t } = useTranslation('onboarding');
  const alert = useAlert();
  const urlSearchParam = useSearch({
    from: '/home/dashboard',
    shouldThrow: false,
  });
  const matchRoute = useMatchRoute();
  const { selectedLocationIds, selectedGroupId } = useAppScopeStore();
  const { setIntakeFormLocationListInfo, setLocationIdForQuery } = useIntakeFormShallowStore(
    'setIntakeFormLocationListInfo',
    'setLocationIdForQuery'
  );

  const selectedParentId = selectedGroupId;
  const selectedLocationId = selectedParentId ?? selectedLocationIds?.[0] ?? '';
  const isParentLocation = !!selectedParentId;
  const isIntakeFormV1Route = !!matchRoute({ to: INTAKE_FORM_PATHS.root as AnyRoute, fuzzy: true });
  const isIntakeFormV2Route = !!matchRoute({ to: INTAKE_FORM_PATH_V2 as AnyRoute });
  const isHomeDashboardPage = !!matchRoute({ to: NWX_DASHBOARD_PATH });
  const navigate = useNavigate();

  const shouldCheckForOnboardingRedirect = isHomeDashboardPage && urlSearchParam?.checkOnboarding === true;

  const intakeFormLocationsQuery = IntakeFormQueries.useIntakeFormLocationsQuery(selectedLocationId);

  useEffect(() => {
    const hasErrorInIntakeFormLocations = intakeFormLocationsQuery.data?.some(({ error }) => !!error);

    // Show alert only in single location or api failure
    if (intakeFormLocationsQuery.isError || (!isParentLocation && hasErrorInIntakeFormLocations)) {
      alert.error(t('Failed to retrieve onboarding form(s) meta data.'));
    }
  }, [
    alert.error,
    intakeFormLocationsQuery.isError,
    intakeFormLocationsQuery.data,
    isParentLocation,
    selectedLocationId,
    intakeFormLocationsQuery.error,
    alert,
    t,
  ]);

  const intakeFormLocationListInfo = useMemo<ReturnType<typeof prepareIntakeFormStoreInfoFromLocationList>>(() => {
    if (intakeFormLocationsQuery.isLoading) {
      return {
        isShowIntakeForm: false,
        isSingleSoftwareOnlyLocation: false,
        isQuoteTypeEventSingleLocation: false,
        isFastPassEligibleLocation: false,
        ownIntakeFormLocations: [],
        isShippingAddressVerified: false,
      };
    }

    const user = getUser();
    const listInfo = prepareIntakeFormStoreInfoFromLocationList({
      intakeFormLocations: intakeFormLocationsQuery.data ?? [],
      userEmail: user?.username,
      isParentLocation,
      isWeaveUser: isWeaveUser(),
    });
    useIntakeFormStore.reset();
    setLocationIdForQuery(selectedLocationId);
    setIntakeFormLocationListInfo(listInfo);
    return listInfo;
  }, [
    intakeFormLocationsQuery.data,
    intakeFormLocationsQuery.isLoading,
    isParentLocation,
    selectedLocationId,
    setIntakeFormLocationListInfo,
    setLocationIdForQuery,
  ]);

  const isLoadingOnboardingInfo = intakeFormLocationsQuery.isLoading;
  const { getFeatureFlagLocationMap } = useScopedAppFlagStore();

  // Use first selected location for feature flag check since parent location flags aren't stored.
  // In multi-location scenarios, all child locations should have consistent feature flag values.
  const locationToCheckFF = selectedLocationIds?.[0];
  const intakeFormV2FFValue = getFeatureFlagLocationMap(INTAKE_FORM_V2_FEATURE_FLAG)?.[locationToCheckFF]?.value;
  const intakeFormV2FFLoading = intakeFormV2FFValue === undefined;
  const hasIntakeFormV2FF = !intakeFormV2FFLoading && intakeFormV2FFValue;

  const isIntakeFormV2Enabled = hasIntakeFormV2FF && intakeFormLocationListInfo.isFastPassEligibleLocation;

  const shouldShowIntakeForm = intakeFormLocationListInfo.isShowIntakeForm;

  const { shouldRedirectToOnboarding, isReady: isOnboardingDashboardConfigReady } = useOnboardingDashboardConfig();

  const onboardingRedirectUrl = useMemo(() => {
    if (isLoadingOnboardingInfo || intakeFormV2FFLoading) {
      return '';
    }
    const isOnIntakeFormRoute = isIntakeFormV2Enabled ? isIntakeFormV2Route : isIntakeFormV1Route;
    const intakeFormPath = isIntakeFormV2Enabled ? INTAKE_FORM_PATH_V2 : INTAKE_FORM_PATHS.root;
    const onboardingDashboardEligible = isOnboardingDashboardConfigReady && shouldRedirectToOnboarding;

    // Redirect to intake form
    if (shouldShowIntakeForm && !isOnIntakeFormRoute) {
      return intakeFormPath;
    }
    // Redirect to Dashboard if on intake form route
    else if (!shouldShowIntakeForm && (isIntakeFormV1Route || isIntakeFormV2Route)) {
      return onboardingDashboardEligible ? ONBOARDING_DASHBOARD_PATH : NWX_DASHBOARD_PATH;
    }
    // Redirect to onboarding dashboard if eligible
    else if (shouldCheckForOnboardingRedirect && onboardingDashboardEligible) {
      return ONBOARDING_DASHBOARD_PATH;
    }
    return '';
  }, [
    intakeFormV2FFLoading,
    isIntakeFormV1Route,
    isIntakeFormV2Enabled,
    isIntakeFormV2Route,
    isLoadingOnboardingInfo,
    isOnboardingDashboardConfigReady,
    shouldCheckForOnboardingRedirect,
    shouldShowIntakeForm,
    shouldRedirectToOnboarding,
  ]);

  const routerSearchParams = useSearch({ strict: false });
  // Remove `checkOnboarding` query param if onboarding dashboard redirection is not needed
  useEffect(() => {
    if (!intakeFormLocationsQuery.isFetched || isLoadingOnboardingInfo || !shouldCheckForOnboardingRedirect) {
      return;
    }
    if (
      !intakeFormLocationListInfo.isFastPassEligibleLocation ||
      (isOnboardingDashboardConfigReady && !shouldRedirectToOnboarding)
    ) {
      navigate({
        to: NWX_DASHBOARD_PATH,
        search: omit(routerSearchParams, ['checkOnboarding']) as AnySearch,
        replace: true,
      });
    }
  }, [
    isOnboardingDashboardConfigReady,
    shouldRedirectToOnboarding,
    shouldCheckForOnboardingRedirect,
    navigate,
    isLoadingOnboardingInfo,
    intakeFormLocationsQuery.isFetched,
    intakeFormLocationListInfo.isFastPassEligibleLocation,
    routerSearchParams,
  ]);

  return {
    shouldShowOnboardingLoader: isLoadingOnboardingInfo,
    onboardingRedirectUrl,
  };
};
